import {
    type EventType,
    type FlightOrderLegChange,
    type FlightOrderPendingChanges,
    type LegChangeKind,
    type ResourceRole,
} from '@eflight/shared';

/** Status of the parent FlightOrder (pilot + duty day). Mirrors the flightOrder lambda. */
export enum FlightOrderStatus {
    approved = 'approved',
    cancelled = 'cancelled',
    partially_approved = 'partially_approved',
    /** Built ahead of time (up to 3 months), not handed to the pilot yet — snapshots are still drafts. */
    pending = 'pending',
    /** Sent to the pilot; from here on the lambda versions snapshot changes instead of rewriting them. */
    sent = 'sent',
}

/**
 * What the CREW MEMBER has answered about a leg. Mirrors the flightOrder lambda, and is kept
 * independent of what happened to the leg itself — see LegState.
 */
export enum ApprovalStatus {
    approved = 'approved',
    /** Nothing is required and nothing ever will be — the pilot never held this leg. */
    cancelled = 'cancelled',
    changed_needs_reapproval = 'changed_needs_reapproval',
    pending = 'pending',
}

/**
 * What happened to the LEG. Written once and never rewritten, so a row can still say "removed on
 * Monday, acknowledged on Tuesday" instead of losing one half of it when the pilot answers.
 */
export enum LegState {
    active = 'active',
    /** Flight cancelled, leg deleted, or this crew member taken off it. */
    removed = 'removed',
}

/**
 * Whether an approval still counts towards its order's rollup — mirror of countsTowardsApproval in
 * the lambda. An unacknowledged removal keeps counting (that is what keeps the order in the
 * notification sweep); once acknowledged it drops out and remains only as history.
 */
export function countsTowardsApproval(approval: { status: string; legState?: string | null }): boolean {
    if (approval.status === ApprovalStatus.cancelled) {
        return false;
    }

    return !(approval.legState === LegState.removed && approval.status === ApprovalStatus.approved);
}

/**
 * A leg the pilot no longer flies and has not acknowledged yet — no snapshotRef, so nothing shows a
 * row for it, while its approval keeps the order unanswered.
 *
 * `countsTowardsApproval` excludes a cancelled approval: a leg never held, not one lost.
 */
export function isUnacknowledgedRemoval(approval: { status: string; legState?: string | null }): boolean {
    return (
        approval.legState === LegState.removed &&
        approval.status !== ApprovalStatus.approved &&
        countsTowardsApproval(approval)
    );
}

export interface CrewRef {
    resourceId: string;
    role: ResourceRole;
    name: string;
}

/**
 * Payload stored in FlightOrderLegSnapshot.data (AWSJSON).
 * Kept in sync with amplify/backend/function/flightOrder/src/models/FlightOrderTypes.ts —
 * the lambda writes it, this screen reads it.
 */
export interface FlightOrderLegSnapshotData {
    legId: string;
    reservationId: string;
    reservationCode?: string;
    eventType?: EventType;
    cancelled: boolean;

    departureLocation?: string;
    arrivalLocation?: string;
    dateFrom: string;
    dateTo: string;
    alternateAirports: string[];

    aircraftId?: string;
    registration?: string;
    aircraftType?: string;

    pic?: CrewRef;
    fo?: CrewRef;

    paxCount: number;
    /** Breakdown behind paxCount — a change notification prints it as "(male/female/child)". */
    paxMale?: number;
    paxFemale?: number;
    paxChild?: number;
    /** Cargo in kg. Absent on snapshots taken before it was captured. */
    cargo?: number;
    /** Animals on board. Absent on snapshots taken before it was captured. */
    pets?: boolean;
    fuelUplift?: number;
    fuelAirport?: string;

    /** Handling agent booked on the leg checklist, per direction; provider id, not name. */
    handlingDepartureProviderId?: string;
    handlingArrivalProviderId?: string;
    /**
     * Catering supplier, per direction. `null` = captured, nobody to name; `undefined` = snapshot
     * predates the field. The only other provider watched for changes — see describeServiceChanges.
     */
    cateringDepartureProviderId?: string | null;
    cateringArrivalProviderId?: string | null;
    /** Fuel supplier pricing the leg; `null` = no quote; `undefined` = predates the field. See legChanges. */
    fuelSupplier?: { provider: string; supplier: string | null } | null;
    /**
     * Checklist services required on the leg, union of both directions — e.g. ["catering", "taxi"].
     * Kept for snapshots older than the per-direction lists below; a change is described from those
     * where they exist, because a service belongs to an AIRPORT and the union cannot say which one.
     */
    services?: string[];
    departureServices?: string[];
    arrivalServices?: string[];

    note?: string;
}

/**
 * What moved on a leg, and what the last change notification told the pilot (written by the
 * flightOrder lambda onto FlightOrder.pendingChanges, cleared when the order is fully confirmed —
 * which is what makes the regenerated PDF come out unhighlighted).
 *
 * Defined in @eflight/shared next to the wording that produces them: these screens diff a leg
 * against the version the pilot approved with the very same code the lambda words its e-mail with.
 */
export type { FlightOrderLegChange, FlightOrderPendingChanges, LegChangeKind };

export interface SnapshotRef {
    legId: string;
    snapshotId: string;
    version: number;
    role: ResourceRole;
}

export interface FlightOrder {
    id: string;
    crewResourceId: string;
    flightDay: string;
    shareToken?: string | null;
    status: string;
    needsApproval?: string | null;
    legCount?: number | null;
    approvedLegCount?: number | null;
    sentAt?: string | null;
    lastNotifiedAt?: string | null;
    snapshotRefs: SnapshotRef[];
    /** Parsed FlightOrder.pendingChanges — undefined when the pilot is up to date. */
    pendingChanges?: FlightOrderPendingChanges;
    lastChangeNotifiedAt?: string | null;
    reminderCount?: number | null;
    /**
     * A hold OPS put on this order when they saved a change: until it passes, the lambda writes
     * nothing to the pilot about it (a cancellation excepted). Cleared by the message that finally
     * goes out — see notificationHeldBack in the flightOrder lambda.
     */
    nextNotificationAt?: string | null;
    /** Amplify optimistic-concurrency version; needed to write the approval rollup back. */
    _version?: number | null;
}

/**
 * Whether this order's notifications are currently on hold — mirror of notificationHeldBack in the
 * lambda. A hold that has already expired is not one: the sweep is free to write again, so a further
 * change is a further decision for OPS to make rather than something already settled.
 */
export function isNotificationHeld(order: Pick<FlightOrder, 'nextNotificationAt'>, now = new Date()): boolean {
    if (!order.nextNotificationAt) {
        return false;
    }
    const until = new Date(order.nextNotificationAt).getTime();

    return !Number.isNaN(until) && now.getTime() < until;
}

/**
 * Rollup status of an order — mirror of computeFlightOrderStatus in the flightOrder lambda
 * (amplify/backend/function/flightOrder/src/models/FlightOrderTypes.ts). `sent` is what separates a
 * pre-built draft (snapshots still rewritten in place) from a document the pilot holds.
 */
export function computeFlightOrderStatus(args: {
    legCount: number;
    approvedCount: number;
    sentAt?: string | null;
}): FlightOrderStatus {
    const { legCount, approvedCount, sentAt } = args;
    if (legCount > 0 && approvedCount === legCount) {
        return FlightOrderStatus.approved;
    }
    if (approvedCount > 0) {
        return FlightOrderStatus.partially_approved;
    }

    return sentAt ? FlightOrderStatus.sent : FlightOrderStatus.pending;
}

/**
 * Recomputes the order rollup from its approvals — the exact mirror of the flightOrder lambda's
 * upsert (FlightOrderProcessor.upsertFlightOrderAndApprovals). Whoever confirms writes this back so
 * the OPS screen and the notification sweep reflect the answer immediately; the 30-min cron is the
 * self-healing backstop if a write races.
 *
 * Lives with the types rather than with the OPS datasource because the pilot app confirms too, from
 * the schedule card, and both sides have to arrive at the same counters.
 */
export function computeOrderRollup(
    order: Pick<FlightOrder, 'sentAt' | 'snapshotRefs' | 'status'>,
    approvals: Array<Pick<FlightOrderLegApproval, 'legState' | 'status'>>
): { approvedLegCount: number; status: string; needsApproval: string | null } {
    const relevant = approvals.filter((a) => countsTowardsApproval(a));
    const approvedCount = relevant.filter((a) => a.status === ApprovalStatus.approved).length;
    // snapshotRefs, not the stored legCount: the latter still counts a leg that has since been
    // removed, which would leave the order stuck at "1 / 2 approved" after the pilot confirmed
    // everything there was to confirm. An unacknowledged removal has no ref left but still has to
    // count, hence the max — otherwise an order that lost a leg reads as fully approved and stops
    // being notified about it.
    const legCount = Math.max(order.snapshotRefs.length, relevant.length);
    const status = computeFlightOrderStatus({ legCount, approvedCount, sentAt: order.sentAt });

    return {
        approvedLegCount: approvedCount,
        // A cancelled day stays cancelled: acknowledging its removal is not the same as it coming
        // back, and the rollup has no way of telling the difference on its own.
        status: order.status === FlightOrderStatus.cancelled ? FlightOrderStatus.cancelled : status,
        // Driven by what is still owed rather than by the derived status: a cancelled day ends up
        // with no legs and no approved count, which is not "approved" but is certainly settled —
        // leaving it flagged would keep it in the notification sweep with nothing left to send.
        needsApproval: relevant.some((a) => a.status !== ApprovalStatus.approved) ? 'x' : null,
    };
}

export interface FlightOrderLegApproval {
    id: string;
    flightOrderId: string;
    legId: string;
    reservationId: string;
    crewResourceId: string;
    role: string;
    status: string;
    /** "active" | "removed" — the leg's own fate, orthogonal to `status`. Absent means active. */
    legState?: string | null;
    removedAt?: string | null;
    latestSnapshotId: string;
    latestVersion: number;
    latestContentHash?: string | null;
    approvedVersion?: number | null;
    approvedContentHash?: string | null;
    approvedAt?: string | null;
    approvedBy?: string | null;
    /**
     * Set when the sync moved the approval onto a later version saying the same thing — a change
     * made after the confirmation and then undone. The pilot has usually had a message about that
     * change, so the leg says so instead of quietly reading "approved" again.
     */
    revertedAt?: string | null;
    /** Amplify optimistic-concurrency version; needed to write the approval back. */
    _version?: number | null;
}

/** A leg of a flight order joined with the crew member's approval of it. */
export interface FlightOrderLeg {
    snapshotId: string;
    version: number;
    /** Role the flight order's pilot holds on this leg. */
    role: ResourceRole;
    data: FlightOrderLegSnapshotData;
    approval?: FlightOrderLegApproval;
    /**
     * The earlier version this leg is measured against — normally the one the pilot approved. Only
     * loaded for a leg awaiting re-approval, and only to say what changed; see attachChangeBaselines.
     */
    baseline?: { version: number; data: FlightOrderLegSnapshotData };
}

/** Everything needed to render one expanded order / one PDF. */
export interface FlightOrderDetail {
    order: FlightOrder;
    legs: FlightOrderLeg[];
}
